import { colors } from '@newfront-insurance/core-ui';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { ArrowUpIcon, Loader2 } from 'lucide-react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface SendButtonProps {
  className?: string;
  disabled?: boolean;
}

/**
 * Send button component for chat input.
 * Uses context to access input state and submit handler.
 * Shows loading state when chat is submitting/streaming.
 */
export function SendButton({ className, disabled: externalDisabled }: SendButtonProps) {
  const { input, handleSubmit, status } = useChatInputContext();
  const { attachments } = useAttachments();
  const { additionalContext } = useChatOverlayContext();

  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const successfulAttachmentUuids = attachments
    .filter((attachment) => attachment.fileUuid && !attachment.uploadError)
    .map((attachment) => attachment.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids)
    ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
    : [];
  const hasUnsyncedAttachmentContext =
    successfulAttachmentUuids.length !== contextAttachmentFileUuids.length ||
    successfulAttachmentUuids.some((uuid) => !contextAttachmentFileUuids.includes(uuid));
  const isDisabled =
    externalDisabled ||
    (input.length === 0 && attachments.length === 0) ||
    hasUploadingFiles ||
    hasUnsyncedAttachmentContext;

  if (['submitted', 'streaming'].includes(status)) {
    return (
      <Button
        data-testid="loading-button"
        className={cn(
          'bg-lime-blue-gradient h-fit rounded-full border-none bg-clip-padding p-2',
          'text-brand-500 hover:opacity-90',
          className,
        )}
        disabled
      >
        <Loader2 size={14} className="animate-spin" />
      </Button>
    );
  }

  return (
    <Button
      data-testid="send-button"
      className={cn(
        'bg-lime-blue-gradient h-fit rounded-full border-none bg-clip-padding p-2',
        'hover:opacity-90',
        'border-transparent',
        className,
        {
          'cursor-not-allowed bg-[#e5e7eb] opacity-50': isDisabled,
        },
      )}
      onClick={(event) => {
        event.preventDefault();
        if (!isDisabled) {
          handleSubmit(undefined);
        }
      }}
      disabled={isDisabled}
    >
      <ArrowUpIcon size={14} color={colors.brand[500]} />
    </Button>
  );
}
